/**
 * DOCUMENTACIÓN TÉCNICA COMPLETA - PLAZA RURAL
 * ==============================================
 * 
 * Esta página contiene documentación exhaustiva y técnica sobre la arquitectura,
 * estructura y funcionamiento completo del sitio web de Plaza Rural.
 * 
 * ACCESO: Solo mediante URL directa - /documentacion-tecnica
 * 
 * CONTENIDO:
 * - Arquitectura general del proyecto
 * - Estructura de carpetas y archivos
 * - Flujo de datos y navegación
 * - Componentes y sus responsabilidades
 * - Tecnologías utilizadas
 * - Guías de desarrollo y mantenimiento
 */

import React, { useState } from 'react';
import { Code, FileText, Folder, Layers, Database, Globe } from 'lucide-react';

const DocumentacionTecnica = () => {
  // Estado para controlar qué sección está expandida
  const [seccionActiva, setSeccionActiva] = useState('arquitectura');

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100 pt-20">
      
      {/* Header de documentación */}
      <div className="bg-gray-800 border-b border-gray-700 py-8">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center gap-4 mb-4">
            <Code className="w-12 h-12 text-green-500" />
            <div>
              <h1 className="text-4xl font-bold">Documentación Técnica</h1>
              <p className="text-gray-400">Plaza Rural - Arquitectura y Estructura del Proyecto</p>
            </div>
          </div>
          <div className="bg-yellow-900/30 border border-yellow-600 rounded-lg p-4 mt-4">
            <p className="text-yellow-300 text-sm">
              <strong>⚠️ ADVERTENCIA:</strong> Esta documentación es confidencial y solo debe ser accedida por el equipo de desarrollo.
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-4 gap-8">
          
          {/* Menú lateral de navegación */}
          <div className="lg:col-span-1">
            <div className="bg-gray-800 rounded-xl p-6 sticky top-24">
              <h3 className="text-lg font-bold mb-4">Índice</h3>
              <nav className="space-y-2">
                {[
                  { id: 'arquitectura', label: 'Arquitectura', icon: Layers },
                  { id: 'estructura', label: 'Estructura', icon: Folder },
                  { id: 'componentes', label: 'Componentes', icon: FileText },
                  { id: 'datos', label: 'Datos y APIs', icon: Database },
                  { id: 'tecnologias', label: 'Tecnologías', icon: Globe }
                ].map(seccion => (
                  <button
                    key={seccion.id}
                    onClick={() => setSeccionActiva(seccion.id)}
                    className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg text-left transition-colors ${
                      seccionActiva === seccion.id
                        ? 'bg-green-600 text-white'
                        : 'text-gray-300 hover:bg-gray-700'
                    }`}
                  >
                    <seccion.icon className="w-5 h-5" />
                    {seccion.label}
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Contenido principal */}
          <div className="lg:col-span-3 space-y-8">
            
            {/* SECCIÓN: ARQUITECTURA GENERAL */}
            {seccionActiva === 'arquitectura' && (
              <div className="space-y-6">
                <div className="bg-gray-800 rounded-xl p-8">
                  <h2 className="text-3xl font-bold mb-6 text-green-400">🏗️ Arquitectura General del Sistema</h2>
                  
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-xl font-semibold mb-3">Stack Tecnológico</h3>
                      <div className="bg-gray-900 rounded-lg p-6">
                        <ul className="space-y-2">
                          <li><strong className="text-green-400">Frontend:</strong> React.js 18+ con Create React App</li>
                          <li><strong className="text-green-400">Enrutamiento:</strong> React Router DOM v6</li>
                          <li><strong className="text-green-400">Estilos:</strong> TailwindCSS 3.x</li>
                          <li><strong className="text-green-400">Componentes UI:</strong> Shadcn/ui</li>
                          <li><strong className="text-green-400">Iconos:</strong> Lucide React</li>
                          <li><strong className="text-green-400">Backend:</strong> FastAPI (Python) - Preparado pero mínimo</li>
                          <li><strong className="text-green-400">Base de Datos:</strong> MongoDB - Preparada para integración futura</li>
                        </ul>
                      </div>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold mb-3">Arquitectura de Componentes</h3>
                      <div className="bg-gray-900 rounded-lg p-6">
                        <p className="mb-4">El proyecto sigue una arquitectura modular basada en componentes reutilizables:</p>
                        <div className="space-y-2 text-sm font-mono">
                          <div className="pl-4 border-l-2 border-green-500">
                            <p>📁 <strong>/src</strong></p>
                            <div className="pl-4">
                              <p>📁 <strong>/components</strong> - Componentes reutilizables</p>
                              <div className="pl-4">
                                <p>📁 <strong>/modern</strong> - Componentes del diseño moderno</p>
                                <p>📁 <strong>/ui</strong> - Componentes base de Shadcn</p>
                              </div>
                              <p>📁 <strong>/pages</strong> - Páginas completas (vistas)</p>
                              <p>📁 <strong>/hooks</strong> - Custom hooks de React</p>
                              <p>📄 <strong>mock.js</strong> - Datos mockeados para desarrollo</p>
                              <p>📄 <strong>App.js</strong> - Componente principal y router</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold mb-3">Flujo de Navegación</h3>
                      <div className="bg-gray-900 rounded-lg p-6">
                        <ol className="space-y-3 list-decimal list-inside">
                          <li><strong>Inicio (/):</strong> Página principal con Hero, sponsors, precios, noticias</li>
                          <li><strong>Remates (/remates):</strong> Información sobre remates (en construcción)</li>
                          <li><strong>Precios (/precios):</strong> Monitor de cotizaciones (en construcción)</li>
                          <li><strong>Noticias (/noticias):</strong> Blog y novedades (en construcción)</li>
                          <li><strong>Contacto (/contacto):</strong> Formulario de contacto (en construcción)</li>
                          <li><strong>Admin Login (/admin/login):</strong> Acceso privado administrativo</li>
                          <li><strong>Documentación (/documentacion-tecnica):</strong> Esta página (oculta)</li>
                        </ol>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* SECCIÓN: ESTRUCTURA DE ARCHIVOS */}
            {seccionActiva === 'estructura' && (
              <div className="space-y-6">
                <div className="bg-gray-800 rounded-xl p-8">
                  <h2 className="text-3xl font-bold mb-6 text-green-400">📁 Estructura de Archivos Detallada</h2>
                  
                  <div className="bg-gray-900 rounded-lg p-6 font-mono text-sm space-y-1 overflow-x-auto">
                    <pre className="text-gray-300">
{`/app
├── frontend/                    # Aplicación React
│   ├── public/                  # Archivos públicos estáticos
│   │   ├── logo.png            # Logo verde de Plaza Rural
│   │   ├── logo_blanco.png     # Logo blanco para header transparente
│   │   └── logos/              # Logos de escritorios/sponsors
│   │       ├── agro.png
│   │       ├── aramburu.png
│   │       ├── arrosa.png
│   │       └── ... (15 logos más)
│   │
│   ├── src/
│   │   ├── components/
│   │   │   ├── modern/         # Componentes del diseño moderno
│   │   │   │   ├── ModernHero.jsx          # Hero principal con carousel
│   │   │   │   ├── ModernHeader.jsx        # Header fijo con nav
│   │   │   │   ├── ModernFooter.jsx        # Footer completo
│   │   │   │   ├── SponsorsBar.jsx         # Barra de logos animada
│   │   │   │   ├── QuickAccessSection.jsx  # Accesos rápidos
│   │   │   │   ├── MarketTicker.jsx        # Ticker de mercados
│   │   │   │   ├── GrainsTicker.jsx        # Ticker de granos
│   │   │   │   ├── LivePricesSection.jsx   # Tabla de precios
│   │   │   │   ├── ModernStats.jsx         # Estadísticas animadas
│   │   │   │   ├── ModernNews.jsx          # Tarjetas de noticias
│   │   │   │   ├── NewsWithSidebar.jsx     # Noticias + cotizaciones
│   │   │   │   └── BannersSection.jsx      # Banners publicitarios
│   │   │   │
│   │   │   └── ui/             # Componentes base (Shadcn)
│   │   │       ├── button.jsx
│   │   │       ├── card.jsx
│   │   │       └── ...
│   │   │
│   │   ├── pages/              # Páginas/Vistas completas
│   │   │   ├── ModernHomePage.jsx      # Página de inicio
│   │   │   ├── AdminLoginPage.jsx      # Login administrativo
│   │   │   ├── RematesPage.jsx         # Página de remates
│   │   │   ├── PreciosPage.jsx         # Página de precios
│   │   │   ├── NoticiasPage.jsx        # Página de noticias
│   │   │   ├── ContactoPage.jsx        # Página de contacto
│   │   │   └── DocumentacionTecnica.jsx # Esta documentación
│   │   │
│   │   ├── hooks/              # Custom React Hooks
│   │   │   └── useScrollReveal.js      # Hook para animaciones al scroll
│   │   │
│   │   ├── mock.js             # Datos mockeados para desarrollo
│   │   ├── App.js              # Componente principal + Router
│   │   ├── App.css             # Estilos globales + keyframes
│   │   ├── index.css           # Estilos base + Tailwind
│   │   └── index.js            # Punto de entrada React
│   │
│   ├── package.json            # Dependencias del proyecto
│   ├── tailwind.config.js      # Configuración de Tailwind
│   └── .env                    # Variables de entorno (backend URL)
│
├── backend/                    # API FastAPI (mínimo por ahora)
│   ├── server.py              # Servidor FastAPI básico
│   ├── requirements.txt       # Dependencias Python
│   └── .env                   # Variables de entorno backend
│
└── README.md                  # Documentación general del proyecto`}
                    </pre>
                  </div>
                </div>
              </div>
            )}

            {/* SECCIÓN: COMPONENTES */}
            {seccionActiva === 'componentes' && (
              <div className="space-y-6">
                <div className="bg-gray-800 rounded-xl p-8">
                  <h2 className="text-3xl font-bold mb-6 text-green-400">🧩 Guía de Componentes</h2>
                  
                  {/* Lista de componentes con descripciones */}
                  <div className="space-y-6">
                    
                    {/* ModernHero */}
                    <div className="bg-gray-900 rounded-lg p-6">
                      <h3 className="text-xl font-semibold text-blue-400 mb-3">ModernHero.jsx</h3>
                      <p className="mb-3"><strong>Propósito:</strong> Sección hero principal con carousel de imágenes y formulario de registro.</p>
                      <div className="space-y-2 text-sm">
                        <p><strong>Props:</strong> Ninguno</p>
                        <p><strong>Estado interno:</strong></p>
                        <ul className="list-disc list-inside pl-4">
                          <li>scrollY - Posición del scroll para parallax</li>
                          <li>currentImageIndex - Índice de la imagen actual del carousel</li>
                          <li>countdown - Objeto con días, horas, minutos, segundos</li>
                        </ul>
                        <p><strong>Características:</strong></p>
                        <ul className="list-disc list-inside pl-4">
                          <li>Carousel automático de 3 imágenes cada 4 segundos</li>
                          <li>Efecto parallax en el scroll</li>
                          <li>Countdown funcional que decrementa cada segundo</li>
                          <li>Formulario de registro con validación</li>
                          <li>Botones de login social (Google y Facebook)</li>
                          <li>Completamente responsive (móvil, tablet, desktop)</li>
                        </ul>
                      </div>
                    </div>

                    {/* ModernHeader */}
                    <div className="bg-gray-900 rounded-lg p-6">
                      <h3 className="text-xl font-semibold text-blue-400 mb-3">ModernHeader.jsx</h3>
                      <p className="mb-3"><strong>Propósito:</strong> Header fijo con navegación y cambio de estilo al hacer scroll.</p>
                      <div className="space-y-2 text-sm">
                        <p><strong>Props:</strong> scrollY (posición del scroll)</p>
                        <p><strong>Características:</strong></p>
                        <ul className="list-disc list-inside pl-4">
                          <li>Fijo en la parte superior (position: fixed)</li>
                          <li>Cambia de transparente a blanco al scrollear</li>
                          <li>Logo cambia de blanco a verde según estado</li>
                          <li>Menú hamburguesa en móvil</li>
                          <li>Links de navegación: Inicio, Remates, Precios, Noticias, Contacto</li>
                          <li>Botón de "Admin" con candado para acceso privado</li>
                          <li>Botón "Ingresar" para usuarios regulares</li>
                        </ul>
                      </div>
                    </div>

                    {/* SponsorsBar */}
                    <div className="bg-gray-900 rounded-lg p-6">
                      <h3 className="text-xl font-semibold text-blue-400 mb-3">SponsorsBar.jsx</h3>
                      <p className="mb-3"><strong>Propósito:</strong> Barra animada con logos de escritorios sponsors.</p>
                      <div className="space-y-2 text-sm">
                        <p><strong>Características:</strong></p>
                        <ul className="list-disc list-inside pl-4">
                          <li>15 logos que se repiten para crear loop infinito</li>
                          <li>Animación CSS continua de derecha a izquierda</li>
                          <li>Contenedores uniformes de 16x24 (h-16 w-24)</li>
                          <li>Fondo degradado verde oscuro</li>
                          <li>Hover effect que aclara el fondo</li>
                        </ul>
                      </div>
                    </div>

                    {/* QuickAccessSection */}
                    <div className="bg-gray-900 rounded-lg p-6">
                      <h3 className="text-xl font-semibold text-blue-400 mb-3">QuickAccessSection.jsx</h3>
                      <p className="mb-3"><strong>Propósito:</strong> Sección de accesos rápidos con 6 categorías.</p>
                      <div className="space-y-2 text-sm">
                        <p><strong>Características:</strong></p>
                        <ul className="list-disc list-inside pl-4">
                          <li>6 tarjetas: Reglamentos, Preoferta, Promedios, Calendarios, Vista Mapa, Preguntas Frecuentes</li>
                          <li>Usa hook useScrollReveal para animación de entrada</li>
                          <li>Animación escalonada (delay incremental por tarjeta)</li>
                          <li>Iconos de Lucide React</li>
                          <li>Hover sutil: scale-105 y translate-y</li>
                          <li>Grid responsive: 2 columnas en móvil, 3 en tablet, 6 en desktop</li>
                        </ul>
                      </div>
                    </div>

                    {/* GrainsTicker */}
                    <div className="bg-gray-900 rounded-lg p-6">
                      <h3 className="text-xl font-semibold text-blue-400 mb-3">GrainsTicker.jsx</h3>
                      <p className="mb-3"><strong>Propósito:</strong> Barra con cotizaciones de granos (Soja, Maíz, Trigo).</p>
                      <div className="space-y-2 text-sm">
                        <p><strong>Características:</strong></p>
                        <ul className="list-disc list-inside pl-4">
                          <li>Animación de entrada con useScrollReveal</li>
                          <li>3 tarjetas con emoji, nombre, valor y cambio porcentual</li>
                          <li>Hover dramático: scale-110, translate-y, cambio de colores</li>
                          <li>Texto cambia a amarillo en hover</li>
                          <li>Icono TrendingUp hace bounce en hover</li>
                          <li>Completamente responsive con grid</li>
                        </ul>
                      </div>
                    </div>

                    {/* LivePricesSection */}
                    <div className="bg-gray-900 rounded-lg p-6">
                      <h3 className="text-xl font-semibold text-blue-400 mb-3">LivePricesSection.jsx</h3>
                      <p className="mb-3"><strong>Propósito:</strong> Tabla completa con precios de remates por categoría.</p>
                      <div className="space-y-2 text-sm">
                        <p><strong>Datos:</strong> Obtiene datos de mock.js (rematesData)</p>
                        <p><strong>Características:</strong></p>
                        <ul className="list-disc list-inside pl-4">
                          <li>Selector de remate (dropdown con R312, R311, etc.)</li>
                          <li>Filtros por categoría: Todos, Terneros, Novillos, Vacas</li>
                          <li>Vista de tabla para desktop</li>
                          <li>Vista de tarjetas compactas para móvil</li>
                          <li>Animación de entrada por fila con delay escalonado</li>
                          <li>Indicadores de tendencia con colores (verde/rojo)</li>
                          <li>Muestra: categoría, cabezas, lotes, máx, mín, promedio, variación</li>
                        </ul>
                      </div>
                    </div>

                    {/* ModernStats */}
                    <div className="bg-gray-900 rounded-lg p-6">
                      <h3 className="text-xl font-semibold text-blue-400 mb-3">ModernStats.jsx</h3>
                      <p className="mb-3"><strong>Propósito:</strong> Estadísticas con animación de conteo.</p>
                      <div className="space-y-2 text-sm">
                        <p><strong>Características:</strong></p>
                        <ul className="list-disc list-inside pl-4">
                          <li>4 estadísticas: Ganaderos Activos, Cabezas Vendidas, Remates Realizados, Países Conectados</li>
                          <li>Componente CountUp que anima números desde 0 hasta el valor final</li>
                          <li>Usa IntersectionObserver para iniciar animación cuando está visible</li>
                          <li>Fondo oscuro con degradado verde</li>
                          <li>Efectos de glow y glassmorphism</li>
                          <li>Hover scale-105</li>
                        </ul>
                      </div>
                    </div>

                    {/* Otros componentes */}
                    <div className="bg-gray-900 rounded-lg p-6">
                      <h3 className="text-xl font-semibold text-blue-400 mb-3">Otros Componentes</h3>
                      <div className="space-y-3 text-sm">
                        <div>
                          <p className="font-semibold">MarketTicker.jsx</p>
                          <p>Ticker animado horizontal con datos de mercados financieros (IBEX, Euro Stoxx, EUR/USD, Oro).</p>
                        </div>
                        <div>
                          <p className="font-semibold">NewsWithSidebar.jsx</p>
                          <p>Sección de noticias con sidebar que muestra cotizaciones y clima.</p>
                        </div>
                        <div>
                          <p className="font-semibold">BannersSection.jsx</p>
                          <p>Banners publicitarios grandes responsive.</p>
                        </div>
                        <div>
                          <p className="font-semibold">ModernFooter.jsx</p>
                          <p>Footer completo con links, redes sociales, newsletter y copyright.</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* SECCIÓN: DATOS Y APIs */}
            {seccionActiva === 'datos' && (
              <div className="space-y-6">
                <div className="bg-gray-800 rounded-xl p-8">
                  <h2 className="text-3xl font-bold mb-6 text-green-400">💾 Datos y Gestión de APIs</h2>
                  
                  <div className="space-y-6">
                    <div className="bg-gray-900 rounded-lg p-6">
                      <h3 className="text-xl font-semibold mb-3">Archivo mock.js</h3>
                      <p className="mb-4">Actualmente, <strong>todos los datos</strong> provienen de mock.js. Este archivo centraliza los datos mockeados que simularán respuestas de APIs en el futuro.</p>
                      
                      <div className="space-y-3 text-sm">
                        <div>
                          <p className="font-semibold text-blue-400">marketData</p>
                          <p>Array con datos de mercados financieros: nombre, valor, cambio, porcentaje de cambio.</p>
                          <p className="text-gray-400">Usado por: MarketTicker.jsx</p>
                        </div>
                        
                        <div>
                          <p className="font-semibold text-blue-400">rematesData</p>
                          <p>Array con información detallada de remates: categoría, cabezas, lotes, precios (max, min, prom), variaciones.</p>
                          <p className="text-gray-400">Usado por: LivePricesSection.jsx</p>
                        </div>
                        
                        <div>
                          <p className="font-semibold text-blue-400">newsData</p>
                          <p>Array con noticias: id, título, categoría, imagen (URL de Unsplash), fecha.</p>
                          <p className="text-gray-400">Usado por: ModernNews.jsx, NewsWithSidebar.jsx</p>
                        </div>
                        
                        <div>
                          <p className="font-semibold text-blue-400">cotizacionesData</p>
                          <p>Objeto con datos históricos de cotizaciones por fecha y categoría.</p>
                          <p className="text-gray-400">Usado por: NewsWithSidebar.jsx (gráfico de cotizaciones)</p>
                        </div>
                        
                        <div>
                          <p className="font-semibold text-blue-400">weatherData</p>
                          <p>Objeto con datos del clima: ubicación, temperatura actual, condición, pronóstico de 5 días.</p>
                          <p className="text-gray-400">Usado por: NewsWithSidebar.jsx (widget de clima)</p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-gray-900 rounded-lg p-6">
                      <h3 className="text-xl font-semibold mb-3">Integración Futura con APIs Reales</h3>
                      <p className="mb-4">Cuando se integre con el backend, seguir este patrón:</p>
                      <div className="bg-gray-950 rounded p-4 font-mono text-xs overflow-x-auto">
<pre>{`// 1. Crear un servicio de API
// /src/services/api.js
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

export const getRemates = async () => {
  const response = await axios.get(\`\${API_BASE_URL}/api/remates\`);
  return response.data;
};

// 2. Usar en el componente
import { getRemates } from '../services/api';

const [rematesData, setRematesData] = useState([]);

useEffect(() => {
  const fetchData = async () => {
    const data = await getRemates();
    setRematesData(data);
  };
  fetchData();
}, []);`}</pre>
                      </div>
                    </div>

                    <div className="bg-gray-900 rounded-lg p-6">
                      <h3 className="text-xl font-semibold mb-3">Variables de Entorno</h3>
                      <p className="mb-4">El proyecto usa variables de entorno para configuración:</p>
                      <div className="space-y-2 text-sm">
                        <div className="bg-gray-950 rounded p-3">
                          <p className="font-semibold text-yellow-400">REACT_APP_BACKEND_URL</p>
                          <p>URL del backend API. Por defecto: http://localhost:8000</p>
                          <p className="text-gray-400 text-xs mt-1">Definido en: /frontend/.env</p>
                        </div>
                        <div className="bg-gray-950 rounded p-3">
                          <p className="font-semibold text-yellow-400">REACT_APP_FACEBOOK_APP_ID</p>
                          <p>ID de la app de Facebook para OAuth (futuro)</p>
                          <p className="text-gray-400 text-xs mt-1">Definido en: /frontend/.env</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* SECCIÓN: TECNOLOGÍAS */}
            {seccionActiva === 'tecnologias' && (
              <div className="space-y-6">
                <div className="bg-gray-800 rounded-xl p-8">
                  <h2 className="text-3xl font-bold mb-6 text-green-400">⚙️ Tecnologías y Herramientas</h2>
                  
                  <div className="space-y-6">
                    {/* React */}
                    <div className="bg-gray-900 rounded-lg p-6">
                      <h3 className="text-xl font-semibold text-cyan-400 mb-3">React 18+</h3>
                      <p className="mb-3">Librería principal para construir la interfaz de usuario.</p>
                      <div className="text-sm space-y-2">
                        <p><strong>Hooks utilizados:</strong></p>
                        <ul className="list-disc list-inside pl-4">
                          <li>useState - Manejo de estado local</li>
                          <li>useEffect - Efectos secundarios y lifecycle</li>
                          <li>useRef - Referencias a elementos DOM</li>
                          <li>useCallback - Memorización de funciones</li>
                          <li>Custom: useScrollReveal - Animaciones al scroll</li>
                        </ul>
                      </div>
                    </div>

                    {/* TailwindCSS */}
                    <div className="bg-gray-900 rounded-lg p-6">
                      <h3 className="text-xl font-semibold text-cyan-400 mb-3">TailwindCSS 3.x</h3>
                      <p className="mb-3">Framework de CSS utility-first para diseño rápido y consistente.</p>
                      <div className="text-sm space-y-2">
                        <p><strong>Configuración personalizada:</strong></p>
                        <ul className="list-disc list-inside pl-4">
                          <li>Colores del tema verde de Plaza Rural</li>
                          <li>Breakpoints responsive estándar</li>
                          <li>Extensiones de animaciones personalizadas</li>
                        </ul>
                        <p><strong>Clases comúnmente usadas:</strong></p>
                        <ul className="list-disc list-inside pl-4">
                          <li>bg-gradient-to-r, bg-gradient-to-br - Degradados</li>
                          <li>backdrop-blur-xl - Efecto glassmorphism</li>
                          <li>transform, translate, scale - Transformaciones</li>
                          <li>transition-all, duration-300 - Transiciones suaves</li>
                        </ul>
                      </div>
                    </div>

                    {/* Lucide React */}
                    <div className="bg-gray-900 rounded-lg p-6">
                      <h3 className="text-xl font-semibold text-cyan-400 mb-3">Lucide React</h3>
                      <p className="mb-3">Librería de iconos moderna y ligera.</p>
                      <div className="text-sm">
                        <p><strong>Iconos más utilizados:</strong></p>
                        <ul className="list-disc list-inside pl-4">
                          <li>Menu, X - Menú hamburguesa</li>
                          <li>Phone, Mail, MapPin - Contacto</li>
                          <li>TrendingUp, TrendingDown - Indicadores de mercado</li>
                          <li>Calendar, Clock - Fechas y horarios</li>
                          <li>Lock, Eye, EyeOff - Autenticación</li>
                        </ul>
                      </div>
                    </div>

                    {/* React Router */}
                    <div className="bg-gray-900 rounded-lg p-6">
                      <h3 className="text-xl font-semibold text-cyan-400 mb-3">React Router DOM v6</h3>
                      <p className="mb-3">Enrutamiento del lado del cliente.</p>
                      <div className="text-sm">
                        <p><strong>Rutas configuradas:</strong></p>
                        <ul className="list-disc list-inside pl-4">
                          <li>/ - Página de inicio (ModernHomePage)</li>
                          <li>/remates - Página de remates</li>
                          <li>/precios - Página de precios</li>
                          <li>/noticias - Página de noticias</li>
                          <li>/contacto - Página de contacto</li>
                          <li>/admin/login - Login administrativo</li>
                          <li>/documentacion-tecnica - Esta documentación</li>
                        </ul>
                      </div>
                    </div>

                    {/* Animaciones */}
                    <div className="bg-gray-900 rounded-lg p-6">
                      <h3 className="text-xl font-semibold text-cyan-400 mb-3">Animaciones CSS</h3>
                      <p className="mb-3">Se usan animaciones CSS personalizadas en App.css:</p>
                      <div className="text-sm">
                        <ul className="list-disc list-inside pl-4">
                          <li>fadeInUp - Fade in desde abajo</li>
                          <li>slideInFromLeft - Slide desde izquierda</li>
                          <li>slideInFromRight - Slide desde derecha</li>
                          <li>zoomIn - Zoom in gradual</li>
                          <li>clipReveal - Reveal con clip-path</li>
                          <li>float - Flotación suave (para partículas)</li>
                          <li>pulse - Pulsación continua</li>
                          <li>rotateIn - Rotación de entrada</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

          </div>
        </div>

        {/* Footer de documentación */}
        <div className="mt-12 bg-gray-800 rounded-xl p-6 text-center">
          <p className="text-gray-400 text-sm">
            📝 Documentación generada automáticamente - Plaza Rural © 2024
          </p>
          <p className="text-gray-500 text-xs mt-2">
            Última actualización: {new Date().toLocaleDateString('es-UY')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default DocumentacionTecnica;
